import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addInstructionToAgent,
  getAgentInstruction,
} from '../../../../services';
import { createInstruction, updateInstruction } from '../../../../services/instructions.js';
import {
  isAgentInstructionNotFoundError,
  parseAgentInstructionBody,
  parseInstructionCreateUuid,
} from '../../../../shared/utils/apiResponse';

const AUTOSAVE_DELAY = 1500;

/**
 * Instructions tab: load agent-linked instruction, edit with debounced autosave (1500ms).
 * Mirrors ann-web-app PlaygroundV2 InstructionsPage behaviour (refs + create → link → update).
 */
export function useAgentInstructionTab({ agentId, agentReady, isNewAgent, t, toast }) {
  const [instruction, setInstruction] = useState('');
  const [instructionUuid, setInstructionUuid] = useState(null);
  const [hasExistingInstruction, setHasExistingInstruction] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const previousInstructionRef = useRef('');
  const hasUnsavedChangesRef = useRef(false);
  const autosaveTimeoutRef = useRef(null);
  const instructionRef = useRef('');
  const instructionUuidRef = useRef(null);
  const hasExistingInstructionRef = useRef(false);
  const isSavingRef = useRef(false);
  /**
   * Text whose save already failed. The autosave is not re-armed for it — only a further edit
   * tries again. Without this a rejected value retried forever (CT-5290).
   */
  const lastFailedValueRef = useRef(null);

  /**
   * `toast` and `t` are read through refs so `saveInstruction` keeps a stable identity. The toast
   * API is memoised on its own item list, so every toast it shows changes its identity — with
   * `toast` in the dependency array each failure re-created `saveInstruction`, which re-ran the
   * autosave effect, which scheduled another failing save (CT-5290).
   */
  const toastRef = useRef(toast);
  const tRef = useRef(t);
  useEffect(() => {
    toastRef.current = toast;
    tRef.current = t;
  }, [toast, t]);

  useEffect(() => {
    instructionRef.current = instruction;
  }, [instruction]);

  useEffect(() => {
    instructionUuidRef.current = instructionUuid;
  }, [instructionUuid]);

  useEffect(() => {
    hasExistingInstructionRef.current = hasExistingInstruction;
  }, [hasExistingInstruction]);

  useEffect(() => {
    if (!agentId || isNewAgent || !agentReady) {
      setInstruction('');
      setInstructionUuid(null);
      setHasExistingInstruction(false);
      setFetchError(null);
      setLoading(false);
      previousInstructionRef.current = '';
      hasUnsavedChangesRef.current = false;
      instructionRef.current = '';
      instructionUuidRef.current = null;
      hasExistingInstructionRef.current = false;
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setFetchError(null);

    getAgentInstruction(agentId)
      .then((res) => {
        if (cancelled) return;
        const body = res?.body ?? res?.data?.body ?? res;
        const parsed = parseAgentInstructionBody(body);
        setInstruction(parsed.instruction);
        setInstructionUuid(parsed.instructionUuid);
        setHasExistingInstruction(Boolean(parsed.instructionUuid));
        previousInstructionRef.current = parsed.instruction.trim();
        hasUnsavedChangesRef.current = false;
        instructionRef.current = parsed.instruction;
        instructionUuidRef.current = parsed.instructionUuid;
        hasExistingInstructionRef.current = Boolean(parsed.instructionUuid);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isAgentInstructionNotFoundError(err)) {
          setInstruction('');
          setInstructionUuid(null);
          setHasExistingInstruction(false);
          previousInstructionRef.current = '';
          hasUnsavedChangesRef.current = false;
          instructionRef.current = '';
          instructionUuidRef.current = null;
          hasExistingInstructionRef.current = false;
          setFetchError(null);
        } else {
          setFetchError(err?.message ?? t('edit.instructions.loadError'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [agentId, agentReady, isNewAgent, t]);

  const saveInstruction = useCallback(async () => {
    if (!agentId || isSavingRef.current) return;

    const current = instructionRef.current.trim();

    // An empty instruction is not persistable — the API answers 400 "Invalid request formats".
    // Nothing is sent, and the panel explains that the last saved instruction still stands, so
    // clearing the field no longer produces a storm of failing requests and toasts (CT-5290).
    if (!current) {
      hasUnsavedChangesRef.current = false;
      return;
    }

    if (
      hasExistingInstructionRef.current &&
      instructionUuidRef.current &&
      current === (previousInstructionRef.current ?? '').trim()
    ) {
      hasUnsavedChangesRef.current = false;
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    const toastId = `agents-instruction-autosave-${agentId}`;
    toastRef.current.info({
      id: toastId,
      title: tRef.current('edit.instructions.autosaveSavingTitle'),
      message: tRef.current('edit.instructions.autosaveSavingMessage'),
      canClose: true,
    });

    let saveSucceeded = false;
    try {
      if (hasExistingInstructionRef.current && instructionUuidRef.current) {
        await updateInstruction(instructionUuidRef.current, { instruction_text: current });
      } else {
        const res = await createInstruction({ instruction_text: current });
        const newUuid = parseInstructionCreateUuid(res);
        if (!newUuid) {
          throw new Error(tRef.current('edit.instructions.createMissingUuid'));
        }
        await addInstructionToAgent(agentId, newUuid);
        instructionUuidRef.current = newUuid;
        hasExistingInstructionRef.current = true;
        setInstructionUuid(newUuid);
        setHasExistingInstruction(true);
      }
      previousInstructionRef.current = current;
      hasUnsavedChangesRef.current = false;
      lastFailedValueRef.current = null;
      saveSucceeded = true;
      toastRef.current.success({
        title: tRef.current('edit.instructions.autosaveSavedTitle'),
        message: tRef.current('edit.instructions.autosaveSavedMessage'),
        canClose: true,
      });
    } catch (err) {
      // Remember what failed so the autosave effect does not re-arm for the same text. One
      // message per rejected value instead of a toast per retry.
      lastFailedValueRef.current = current;
      hasUnsavedChangesRef.current = false;
      toastRef.current.error({
        title: tRef.current('edit.instructions.autosaveErrorTitle'),
        message: err?.message ?? tRef.current('edit.instructions.autosaveErrorMessage'),
        canClose: true,
      });
    } finally {
      // The progress toast has served its purpose once the result is in — leaving it up stacked
      // "Saving…" beside "Could not save".
      toastRef.current.close?.(toastId);
      isSavingRef.current = false;
      setIsSaving(false);
    }

    if (saveSucceeded) {
      const stillDirty =
        hasUnsavedChangesRef.current &&
        instructionRef.current.trim() !== (previousInstructionRef.current ?? '').trim();
      if (stillDirty) {
        queueMicrotask(() => {
          void saveInstruction();
        });
      }
    }
  }, [agentId]);

  const handleInstructionChange = useCallback((value) => {
    setInstruction(value);
    instructionRef.current = value;
    hasUnsavedChangesRef.current = true;
    // Editing past a rejected value clears the block, so the next autosave runs normally.
    if (value.trim() !== lastFailedValueRef.current) lastFailedValueRef.current = null;
  }, []);

  useEffect(() => {
    if (!agentId || isNewAgent || !agentReady) return undefined;
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }
    if (!hasUnsavedChangesRef.current) return undefined;
    const trimmed = instruction.trim();
    if (trimmed === (previousInstructionRef.current ?? '').trim()) return undefined;
    // Nothing to send for an empty instruction, and nothing to gain from re-sending a value the
    // API already rejected — both used to schedule a save that could only fail again (CT-5290).
    if (!trimmed) return undefined;
    if (trimmed === lastFailedValueRef.current) return undefined;

    autosaveTimeoutRef.current = window.setTimeout(() => {
      autosaveTimeoutRef.current = null;
      void saveInstruction();
    }, AUTOSAVE_DELAY);

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
        autosaveTimeoutRef.current = null;
      }
    };
  }, [agentId, agentReady, isNewAgent, instruction, saveInstruction]);

  return {
    instruction,
    handleInstructionChange,
    loading,
    fetchError,
    isSaving,
    /**
     * The field has been emptied but a saved instruction exists. Nothing is sent (the API rejects
     * an empty instruction), so the panel says which instruction is still in effect rather than
     * leaving the blank field looking saved.
     */
    showEmptyNotice: hasExistingInstruction && !instruction.trim(),
  };
}
